import React, { useEffect, useState, useContext } from 'react'
import { useLoaderData, useParams } from 'react-router-dom'
import { useAuth } from '../../../../../hooks/auth'

import { Contract, ContractPosition, ContractPerformance } from '../../types'

import PerformanceStage from './components/PerformanceStage'
import PerformanceInfo from './components/PerformanceInfo'
import PerformanceBaseInfo from './components/PerformanceBaseInfo'
import PerformanceDocs from './components/PerformanceDocs'
import PerformancePayments from './components/PerformancePayments'

import PerformanceDocuments from './components/PerformanceDocuments'

import Footer from './components/Footer'

import { Structure } from '../../../Org/Structure/types'
import {
    CurrencyList,
    PerformanceBaseInfoError,
    PerformanceDocsError,
    PerformancePaymentsError,
    PerformanceDocumentsError,
    PerformanceEditErrors,
    ContractPerformanceDoc,
} from './types'

import { User } from '../../../Org/Users/types'
import { OrganizationInfo } from '../../../Org/Home/types'

import { ThemeContext } from '../../../../../components/ThemeContext'

import HoverTooltip from '../../../../../components/HoverTooltip'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

export const loadPerformances = async ({
    params,
}: any): Promise<ContractPerformance | null> => {
    const action = params.action
    const contract_reg_number = params.contract_reg_number
    const perf_action = params.perf_action
    const perf_guid = params.perf_guid

    // Для create — ничего не грузим
    if (!perf_guid) return null

    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract/guid/${perf_guid}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch performance_contract', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const performance: ContractPerformance | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно контракт
    if (!performance || !('guid' in performance)) return null

    return performance
}

function getCurrentYear() {
    const today = new Date()
    return today.getFullYear() // Возвращает текущий год
}

function getCurrentMonth() {
    const today = new Date()
    return today.getMonth() // Возвращает номер месяца (0–11)
}

const getContract = async (
    contract_reg_number: string | null
): Promise<Contract | null> => {
    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/contract/contract_reg_number/${contract_reg_number}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch contract', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const contract: Contract | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно контракт
    if (!contract || !('guid' in contract)) return null

    return contract
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

const ContractPerformanceEditPage: React.FC = () => {
    const loadedPerformance = useLoaderData() as ContractPerformance
    const [performance, setPerformance] =
        useState<ContractPerformance>(loadedPerformance)

    const [contract, setContract] = useState<Contract | null>(null)
    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const { user } = useAuth()
    const { theme } = useContext(ThemeContext)

    const { contract_reg_number: contract_reg_number } = useParams()
    const { action: action } = useParams()
    const { perf_guid: perf_guid } = useParams()
    const { perf_action: perf_action } = useParams()

    const [contractGuid, setContractGuid] = useState('')

    const [isUserRoleName, setUserRoleName] = useState('')
    const [structList, setStructList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])

    const [tab, setTab] = useState<string>('Исполнение договора')

    const [isUserStructGuid, setUserStructGuid] = useState('')
    const [isUserStructName, setUserStructName] = useState('')

    const [positions, setPositions] = useState<ContractPosition[] | null>(null)
    const [positionsLoadedGuid, setPositionsLoadedGuid] = useState<
        string | null
    >(null)
    const [positionsLoading, setPositionsLoading] = useState(false)
    const [positionsError, setPositionsError] = useState<string | null>(null)

    const [performances, setPerformances] = useState<
        ContractPerformance[] | null
    >(null)
    const [performancesLoadedGuid, setPerformancesLoadedGuid] = useState<
        string | null
    >(null)
    const [performancesLoading, setPerformancesLoading] = useState(false)
    const [performancesError, setPerformancesError] = useState<string | null>(
        null
    )

    const [errors, setErrors] = useState<PerformanceEditErrors>({})
    const [errorPopup, setErrorPopup] = useState(false)
    const [isHaveError, setHaveError] = useState<boolean>(true)

    const [havePerformanceDocs, setHavePerformanceDocs] =
        useState<boolean>(false)
    const [havePerformancePayments, setHavePerformancePayments] =
        useState<boolean>(false)
    const [havePerformanceDocuments, setHavePerformanceDocuments] =
        useState<boolean>(false)

    // сбрасываем кеш при смене договора
    useEffect(() => {
        setPerformance(loadedPerformance ?? null)
        // тут же можно сбрасывать связанные состояния
        setPositions(null)
        setPositionsLoadedGuid(null)
        setPositionsError(null)
    }, [loadedPerformance?.guid])

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const contractData = await getContract(
                    contract_reg_number ?? null
                )
                if (contractData) {
                    setContract(contractData)
                    setContractGuid(contractData.guid)
                }

                const structs = await getStructureList()
                if (Array.isArray(structs)) {
                    setStructList(structs)
                    if (structs) {
                        const matchingStruct = structs.find(
                            (struct) =>
                                struct.guid.toLowerCase() ===
                                user?.object.struct_id
                        )

                        const structShortName =
                            matchingStruct?.struct_short_name || ''
                        setUserStructName(structShortName)
                        const structGuid = matchingStruct?.guid || ''
                        setUserStructGuid(structGuid)

                        // Только для создания — проставляем дефолт
                        // if (action === 'create') {
                        //     setPerformance((prev) => ({
                        //         ...prev,
                        //         struct_guid: prev.struct_guid ?? structGuid,
                        //         struct_name:
                        //             prev.struct_name ?? structShortName,
                        //     }))
                        // }
                    }
                } else {
                    console.error('Нет данных:', structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    // Функция валидации, которая собирает ошибки для всех компонентов
    const validatePerformanceForm = (): boolean => {
        // const plannedYear = getStartDate(pos).getFullYear()
        // const reportingYear = plan[0]?.reporting_year
        // const skipValidation =
        //     (reportingYear &&
        //         plannedYear - reportingYear >= 1 &&
        //         pos.is_purchase_smb === true) ||
        //     pos.planned_after_second_year === true

        const performanceBaseInfoError: PerformanceBaseInfoError = {}
        const performanceDocsError: PerformanceDocsError = {}
        const performancePaymentsError: PerformancePaymentsError = {}
        const performanceDocumentsError: PerformanceDocumentsError = {}

        // Проверяем информацию об изменении, если это изменение
        if (
            performance.completion_stage_end_date == '' ||
            performance.completion_stage_end_date == null
        ) {
            performanceBaseInfoError.completion_stage_end_date =
                '"Дата окончания исполнения договора (отдельного этапа исполнения договора)" - Поле обязательно к заполнению'
        }

        if (performance.has_penalty) {
            if (
                !performance.penalty_info ||
                performance.penalty_info.trim() === ''
            ) {
                performanceBaseInfoError.penalty_info =
                    '"Информация о начислении неустоек (штрафов, пеней)" - Поле обязательно к заполнению'
            }
        }

        if (havePerformanceDocs == false && havePerformancePayments == false) {
            performanceDocsError.have_docs_items =
                'Информация о документах приемки обязателена, если не заполнена информация об оплате'
            performancePaymentsError.have_payments_items =
                'Информация об оплате обязателена, если не заполнена информация о документах приемки'
        }

        if (havePerformanceDocuments == false) {
            performanceDocumentsError.have_documents_items =
                'Документы исполнения обязательны'
        }

        setErrors({
            performanceBaseInfo: performanceBaseInfoError,
            performanceDocs: performanceDocsError,
            performancePayments: performancePaymentsError,
            performanceDocuments: performanceDocumentsError,
        })

        // Если роль admin или specialist, учитываем оба блока, иначе только purchaseContract
        const isPerformanceBaseInfoValid =
            Object.keys(performanceBaseInfoError).length === 0
        const isPerformanceDocsValid =
            Object.keys(performanceDocsError).length === 0
        const isPerformancePaymentsValid =
            Object.keys(performancePaymentsError).length === 0
        const isPerformanceDocumentsValid =
            Object.keys(performanceDocumentsError).length === 0

        // Сохраение после проверки
        // handleSave()

        setErrorPopup(true)
        console.log(errors)

        return (
            isPerformanceBaseInfoValid &&
            isPerformanceDocsValid &&
            isPerformancePaymentsValid &&
            isPerformanceDocumentsValid
        )
    }

    const handleValidation = () => {
        if (validatePerformanceForm()) {
            // Если форма валидна, можно выполнять дальнейшие действия (например, отправку данных)
            setHaveError(false)
            console.log('Форма валидна')
        } else {
            setHaveError(true)
            console.log('Найдены ошибки в форме')
        }
    }

    // Рекурсивная функция для обхода объекта ошибок и сбора строковых сообщений
    const flattenErrors = (obj: any): string[] => {
        let errorList: string[] = []
        Object.keys(obj).forEach((key) => {
            const value = obj[key]
            if (typeof value === 'string') {
                errorList.push(value)
            } else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (typeof item === 'string') {
                        errorList.push(item)
                    } else if (typeof item === 'object' && item !== null) {
                        errorList = errorList.concat(flattenErrors(item))
                    }
                })
            } else if (typeof value === 'object' && value !== null) {
                errorList = errorList.concat(flattenErrors(value))
            }
        })
        return errorList
    }

    // Функция для получения строки ошибок в нужном порядке, с изменённым именем переменной
    const getErrorsText = (errorObj: PerformanceEditErrors): string => {
        // Задаём порядок вывода ошибок
        const order: (keyof PerformanceEditErrors)[] = [
            'performanceBaseInfo',
            'performanceDocs',
            'performancePayments',
            'performanceDocuments',
        ]

        let errorList: string[] = []
        order.forEach((key) => {
            if (errorObj[key]) {
                errorList = errorList.concat(flattenErrors(errorObj[key]))
            }
        })
        // Объединяем каждое сообщение с новой строки
        return errorList.join('\n\n')
    }

    const handleErrorPopup = () => {
        setErrorPopup(!errorPopup)
    }

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-fit items-center justify-center">
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <PerformanceStage
                        contractPerformance={performance}
                        theme={theme}
                    />
                </div>
                <div className="flex flex-col w-full min-h-[100px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PerformanceInfo
                        contract={contract}
                        contractPerformance={performance}
                        theme={theme}
                        currencyList={currencyList}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_250px)] items-center justify-center gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818] ">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full min-h-0 gap-y-12 items-start justify-start overflow-x-visible overflow-y-auto custom-scrollbar">
                    <fieldset
                        className={`flex flex-col w-full gap-10`}
                        disabled={false}
                    >
                        <PerformanceBaseInfo
                            perfAction={
                                perf_action == 'edit' ||
                                performance.performance_stage ==
                                    'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            contractEndExecutionDate={
                                contract?.end_execution_date ?? null
                            }
                            theme={theme}
                            performances={performances ?? []}
                            performancesLoading={performancesLoading}
                            performancesError={performancesError}
                            currencyList={currencyList}
                            contractRubPrice={contract?.rub_price}
                            contractPrice={contract?.price}
                            performance={performance}
                            onCompletedChange={(completed) => {
                                setPerformance((prev) => ({
                                    ...prev,
                                    completed: completed,
                                }))
                            }}
                            isPenalty={performance?.has_penalty}
                            onPenaltyChange={(penalty) => {
                                setPerformance((prev) => ({
                                    ...prev,
                                    has_penalty: penalty,
                                }))
                                if (penalty == false) {
                                    setPerformance((prev) => ({
                                        ...prev,
                                        penalty_info: null,
                                    }))
                                }
                                setHaveError(true)
                            }}
                            penaltyInfo={performance?.penalty_info}
                            onPenaltyInfoChange={(penalty_info) => {
                                setPerformance((prev) => ({
                                    ...prev,
                                    penalty_info: penalty_info,
                                }))
                                setHaveError(true)
                            }}
                            onCompletionStageEndDate={(
                                completion_stage_end_date
                            ) => {
                                setPerformance((prev) => ({
                                    ...prev,
                                    completion_stage_end_date:
                                        completion_stage_end_date,
                                }))
                                setHaveError(true)
                            }}
                            errors={errors.performanceBaseInfo}
                        />

                        <PerformanceDocs
                            theme={theme}
                            perfAction={
                                perf_action == 'edit' ||
                                performance.performance_stage ==
                                    'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contractGuid}
                            contractRegNumber={contract_reg_number}
                            performance={performance}
                            performanceGuid={perf_guid}
                            currencyList={currencyList}
                            performanceCurrency={{
                                currencyCharCode:
                                    contract?.currency_code ?? null,
                                currencyName: contract?.currency_name ?? null,
                                currencyDigitalCode:
                                    contract?.currency_digital_code ?? null,
                            }}
                            onPerformanceDocs={(have_perf_docs) =>
                                setHavePerformanceDocs(have_perf_docs)
                            }
                            errors={errors.performanceDocs}
                            onAnyEdit={() => setHaveError(true)}
                        />

                        <PerformancePayments
                            theme={theme}
                            perfAction={
                                perf_action == 'edit' ||
                                performance.performance_stage ==
                                    'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contractGuid}
                            contractRegNumber={contract_reg_number}
                            performance={performance}
                            performanceGuid={perf_guid}
                            currencyList={currencyList}
                            performanceCurrency={{
                                currencyCharCode:
                                    contract?.currency_code ?? null,
                                currencyName: contract?.currency_name ?? null,
                                currencyDigitalCode:
                                    contract?.currency_digital_code ?? null,
                            }}
                            onTotalChange={(total) => {
                                setPerformance((prev) => ({
                                    ...prev,
                                    total: total,
                                }))
                            }}
                            onPerformancePayments={(have_perf_payments) =>
                                setHavePerformancePayments(have_perf_payments)
                            }
                            errors={errors.performancePayments}
                            onAnyEdit={() => setHaveError(true)}
                        />

                        <PerformanceDocuments
                            theme={theme}
                            perfAction={
                                perf_action == 'edit' ||
                                performance.performance_stage ==
                                    'Отправка в ЕИС'
                                    ? true
                                    : false
                            }
                            contractGuid={contract?.guid}
                            performance={performance}
                            performanceGuid={perf_guid}
                            role={isUserRoleName}
                            onPerformanceDocuments={(have_perf_documents) =>
                                setHavePerformanceDocuments(have_perf_documents)
                            }
                            errors={errors.performanceDocuments}
                            onAnyEdit={() => setHaveError(true)}
                        />
                    </fieldset>
                </div>
            </div>

            <Footer
                theme={theme}
                perfAction={perf_action == 'edit' ? true : false}
                contractGuid={contractGuid}
                performanceGuid={perf_guid}
                performance={performance}
                user_guid={user?.object.user_id}
                pos_user_guid={performance.user_guid}
                pos_supervisor_user_guid={performance.supervisor_user_guid}
                role={isUserRoleName}
                stage={performance.performance_stage}
                status={performance.performance_status}
                handleValidation={handleValidation}
                isHaveError={isHaveError}
                onPerformanceStatus={(performance_status) => {
                    setPerformance((prev) => ({
                        ...prev,
                        performance_status: performance_status,
                    }))
                }}
                onPerformanceStage={(performance_stage) => {
                    setPerformance((prev) => ({
                        ...prev,
                        performance_stage: performance_stage,
                    }))
                }}
            />

            {errorPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50"
                    onClick={handleErrorPopup}
                >
                    <div
                        className={`p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] ${
                            !isHaveError ? 'w-[500px]' : 'w-[650px]'
                        }  h-fit`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            {!isHaveError ? (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#187A33"
                                        d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                                    ></path>
                                </svg>
                            ) : (
                                <svg
                                    className="w-5 h-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    id="times-circle"
                                >
                                    <path
                                        fill="#C15C5C"
                                        d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                    ></path>
                                </svg>
                            )}

                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                {!isHaveError
                                    ? 'Проверка прошла успешно'
                                    : 'Найдены ошибки'}
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            {!isHaveError ? (
                                <div className="flex flex-col w-full space-y-1">
                                    <div>
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF] text-left">
                                            Ошибок нет. Для дальнейшей работы
                                            нажмите на кнопку «Готово».
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col w-full space-y-1 gap-2">
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <label className="text-[12.8px] text-[#181818] dark:text-[#FFFFFF] text-left">
                                            Внимание! Для дальнейшей работы
                                            необходимо исправить ошибки в
                                            следующих полях:
                                        </label>
                                    </div>
                                    <div className="flex flex-col mt-2 space-y-1">
                                        <textarea
                                            className="form-textarea flex w-full h-full text-[11px] items-start justify-start p-2 rounded-md border-0"
                                            value={getErrorsText(errors)}
                                            rows={21}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleErrorPopup}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ContractPerformanceEditPage
